import { useQuery } from "@tanstack/react-query";
import { Alert, Button, Card, Stack, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@features/auth/context/AuthContext";
import { hasAssignedRole } from "@web/config/roles";
import { hasPermission } from "@web/config/permissions";
import { apiClient } from "@lib/api/apiClient";
import type { ApiResponse } from "@lib/api/api-types";
import { SalesStructure, type StructurePerson } from "./SalesStructure";
type PeoplePage = {
  items: StructurePerson[];
  pagination: { totalPages: number };
};
async function load(endpoint: string) {
  const people: StructurePerson[] = [];
  for (let page = 1; ; page++) {
    const response = await apiClient.get<ApiResponse<PeoplePage>>(
      `/sales/reporting/${endpoint}`,
      { params: { page, limit: 100 } },
    );
    people.push(...response.data.data.items);
    if (page >= response.data.data.pagination.totalPages) return people;
  }
}
export function SalesDirectorPage() {
  const { user } = useAuth();
  return user &&
    hasAssignedRole(user, "SALES_DIRECTOR") &&
    hasPermission(user.role, "leads:view", user.permissions) && user.permissions.some(p=>p==="leads:view_team" || p==="leads:view_all") ? (
    <DirectorTeams userId={user.id} />
  ) : (
    <Alert severity="error">Sales Director access required.</Alert>
  );
}
function DirectorTeams({ userId }: { userId: number }) {
  const navigate = useNavigate();
  const query = useQuery({
    queryKey: ["sales-reporting", "director-teams", userId],
    queryFn: async () => {
      const [directors, managers, consultants] = await Promise.all([
        load("directors"),
        load("managers"),
        load("consultants"),
      ]);
      return { directors, managers, consultants };
    },
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
  });
  return (
    <Stack spacing={2}>
      <Typography variant="h4">Sales Teams</Typography>
      <Typography color="text.secondary">
        Sales Director oversight of all Sales Managers, Sales Consultants and
        their lead updates.
      </Typography>
      <Stack direction="row" spacing={1}>
        <Button variant="contained" onClick={() => navigate("/leads")}>
          View division leads
        </Button>
        <Button onClick={() => navigate("/follow-ups")}>View follow-ups</Button>
        <Button
          disabled={query.isFetching}
          onClick={() => void query.refetch()}
        >
          Refresh teams
        </Button>
      </Stack>
      <Card sx={{ p: 2 }}>
        {query.isLoading ? (
          <Typography role="status">Loading sales teams…</Typography>
        ) : query.isError ? (
          <Alert severity="error">{query.error.message}</Alert>
        ) : (
          query.data && (
            <SalesStructure
              {...query.data}
              readOnly
              onViewLeads={(person) =>
                navigate(`/leads?assignedUserId=${person.id}`)
              }
            />
          )
        )}
      </Card>
    </Stack>
  );
}
